import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares';

// Event emitter.
const customInsightsEvents = {
  handler: null,
  emit(event) {
    if (this.handler) {
      this.handler(event, window.aa);
    }
  },
  setHandler(handler) {
    this.handler = handler;
  }
};

/**
 * Type-specific configurations
 */
const typeConfigs = {
  Course: {
    clickEventName: 'Course Clicked',
    hasAccordions: true,
    accordionEventName: 'Course Accordion Opened',
  }
};

/**
 * Valid type names.
 */
const validHitTypes = [
  'course',
  'department',
  'event',
  'minor',
  'page',
  'person',
  'policy',
  'news',
  'post',
  'prattfolio-story',
  'prattfolio',
  'press',
  'program',
  'resource',
  'service',
  'work',
  'daily-hub',
];

/**
 * Create insights middleware with type-specific behavior
 * @param {string} type - The content type (Course, Article, Resource, etc.)
 * @returns {InternalMiddleware}
 */
export const createMiddleware = (type) => {
  const config = typeConfigs[type] || {
    clickEventName: `${type} Clicked`,
    hasAccordions: false,
  };

  let hasViewedHits = false;
  let justOpenedAccordion = false;

  // Single handler for ALL events (automatic and custom)
  const onEventHandler = (event, aa) => {
    const { insightsMethod, payload, widgetType } = event;

    if (insightsMethod === 'viewedObjectIDs') {
      if (hasViewedHits) {
        return;
      }

      hasViewedHits = true;
    }

    // Replace default "Hit Clicked" with type-specific name
    if (widgetType === 'ais.hits' && payload.eventName === 'Hit Clicked') {
      if (justOpenedAccordion) {
        return;
      }

      let hitType = type;

      if (payload.objectIDs && payload.objectIDs.length > 0) {
        const objectID = payload.objectIDs[0];
        const parts = objectID.split('-');
        const extractedType = parts[1];

        if (extractedType && validHitTypes.includes(extractedType)) {
          if ( 'post' === extractedType ) {
            hitType = 'news';
          } else {
            hitType = extractedType;
          }
        }
      }

      // Capitalize first letter for event name
      const capitalizedType = hitType.charAt(0).toUpperCase() + hitType.slice(1);
      payload.eventName = `${capitalizedType} Clicked`;
    }

    // Handle custom accordion events
    if (widgetType === 'custom.accordion') {
      if (config.hasAccordions) {
        payload.eventName = config.accordionEventName || `${type} Accordion Opened`;

        // Set flag to prevent duplicate hit event.
        justOpenedAccordion = true;

        // Remove flag after brief timeout, so that clicking within accordion is not blocked.
        setTimeout(() => {
          justOpenedAccordion = false;
        }, 100);
      } else {
        // This type shouldn't have accordion events
        console.warn(`Accordion event for type ${type} which doesn't support accordions`);
        return;
      }
    }

    // Send to Algolia
    if (insightsMethod && aa) {
      aa(insightsMethod, payload);
    }
  };

  // Create middleware with the handler
  const middleware = createInsightsMiddleware({
    insightsClient: window.aa,
    insightsInitParams: {
      appId: window.pratt.env.ALGOLIA_APPLICATION_ID,
      apiKey: window.pratt.env.ALGOLIA_SEARCH_API_KEY,
      useCookie: false,
    },
    onEvent: onEventHandler,
  });

  // Connect custom event emitter to the same handler
  customInsightsEvents.setHandler(onEventHandler);

  return middleware;
};

/**
 * @param {HTMLElement} button - The clicked element
 * @returns {boolean} - True if already open
 */
const checkClosed = (button) => {
  if (!button) {
    return false;
  }

  return button.getAttribute('aria-expanded') === 'false';
}

/**
 * Setup accordion tracking with inline sendEvent
 * @param {HTMLElement} container - Container element to attach listeners to
 * @param {string} indexName - Algolia index name
 */
export const setupAccordionTracking = (container, indexName) => {
  if (!container) {
    return;
  }

  // Track accordion expansions
  container.addEventListener('click', (e) => {
    const accordionButton = e.target.closest('.js-accordion-trigger');

    if (accordionButton && checkClosed(accordionButton)) {
      const eventType = accordionButton.dataset.eventType || 'click';
      const eventName = accordionButton.dataset.eventName;
      const objectID = accordionButton.dataset.objectId;
      const queryID = accordionButton.dataset.queryId;
      const position = accordionButton.dataset.position;

      // Validate and send event
      if (eventName && objectID && queryID && position && typeof window.aa !== 'undefined') {
        const parsedPosition = parseInt(position, 10);

        if (!isNaN(parsedPosition)) {
          customInsightsEvents.emit({
            insightsMethod: 'clickedObjectIDs',
            payload: {
              eventType: eventType,
              eventName: eventName,
              index: indexName,
              objectIDs: [objectID],
              queryID: queryID,
              positions: [parsedPosition],
            },
            widgetType: 'custom.accordion',
            eventType: eventType,
          });
        }
      }
    }
  }, true); // Use capture phase
};
